import { type FC, useLayoutEffect, useRef } from 'react'

import { useGlobalVideo } from './globalVideoContext'
import type { CloudFrontCredentials } from 'helpers/cloudfrontCookies'

interface Props {
  ownerType: 'post' | 'preview' | 'message' | 'story'
  ownerId: string
  mediaId: string
  videoId?: string
  videoUserId?: string
  url: string
  poster?: string
  active: boolean
  className?: string
  pauseAndStopLoadingOnDetach?: boolean
  isFeedHost?: boolean
  cloudfront?: CloudFrontCredentials | null

  onPlay?: () => void
  onPause?: () => void
  onEnded?: () => void
}

const VideoSurface: FC<Props> = ({
  ownerType,
  ownerId,
  mediaId,
  videoId,
  videoUserId,
  url,
  cloudfront,
  poster,
  active,
  className,
  pauseAndStopLoadingOnDetach = false,
  isFeedHost = false,
  onPlay,
  onPause,
  onEnded
}) => {
  const hostRef = useRef<HTMLDivElement>(null)

  const onPlayRef = useRef(onPlay)
  const onPauseRef = useRef(onPause)
  const onEndedRef = useRef(onEnded)
  const {
    prepare,
    attach,
    detach,
    pause,
    stopLoading,
    getVideoElement,
    getCurrentOwner,
    getCurrentMedia
  } = useGlobalVideo()

  useLayoutEffect(() => {
    onPlayRef.current = onPlay
    onPauseRef.current = onPause
    onEndedRef.current = onEnded
  }, [
    onPlay,
    onPause,
    onEnded
  ])

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || !url) return

    prepare({
      owner: {
        type: ownerType,
        id: ownerId
      },
      host,
      media: {
        id: mediaId,
        url,
        poster,
        videoId,
        videoUserId,
        cloudfront
      },
      isFeedHost
    })
  }, [
    prepare,
    ownerType,
    ownerId,
    mediaId,
    videoId,
    videoUserId,
    url,
    cloudfront,
    poster,
    isFeedHost
  ])

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!active || !host || !url) return

    const owner = {
      type: ownerType,
      id: ownerId
    } as const

    attach({
      owner,
      host,
      media: {
        id: mediaId,
        url,
        poster,
        videoId,
        cloudfront,
        videoUserId
      },
      isFeedHost
    })

const video = getVideoElement(mediaId, url)

    const handlePlay = () => {
      onPlayRef.current?.()
    }

    const handlePause = () => {
      onPauseRef.current?.()
    }

    const handleEnded = () => {
      onEndedRef.current?.()
    }

    if (video) {
      video.addEventListener(
        'play',
        handlePlay
      )

      video.addEventListener(
        'pause',
        handlePause
      )

      video.addEventListener(
        'ended',
        handleEnded
      )

      // Sync immediately in case play happened before
      // this surface attached its listeners.
      if (video.ended) {
        handleEnded()
      } else if (video.paused) {
        handlePause()
      } else {
        handlePlay()
      }
    }

    return () => {

      if (video) {
        video.removeEventListener(
          'play',
          handlePlay
        )
        video.removeEventListener(
          'pause',
          handlePause
        )

        video.removeEventListener(
          'ended',
          handleEnded
        )
      }

      if (pauseAndStopLoadingOnDetach) {
        const currentOwner = getCurrentOwner()
        const currentMedia = getCurrentMedia()

        const stillOwnsCurrentPlayer =
          currentOwner?.type === owner.type &&
          currentOwner?.id === owner.id &&
          currentMedia?.id === mediaId &&
          currentMedia?.url.split('?')[0] === url.split('?')[0]

        if (stillOwnsCurrentPlayer) {
          pause()
          stopLoading()
        }
      }

      detach(owner, host)
    }
  }, [
    active,
    ownerType,
    ownerId,
    mediaId,
    videoId,
    videoUserId,
    url,
    cloudfront,
    poster,
    pauseAndStopLoadingOnDetach,
    isFeedHost,
    attach,
    detach,
    pause,
    stopLoading,
    getVideoElement,
    getCurrentOwner,
    getCurrentMedia
  ])

  return (
    <div
      ref={hostRef}
      className={`global-video-surface${
        className ? ` ${className}` : ''
      }`}
      data-global-video-owner={ownerType}
      data-global-video-id={mediaId}
    />
  )
}

export default VideoSurface